/**
 * 炼器炉面板
 */

import type { PlayerState } from '../engine/Player';
import { FORGE_RECIPES } from '../data/forgeRecipes';
import { calcForgeRate, canForge, hasForgeIngredients, getForgeLevel, getForgeProgress } from '../engine/ForgeSystem';

export type ForgeAction = 'craft' | 'close';
export interface ForgeActionPayload { action: ForgeAction; recipeId?: string; }

export class ForgePanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: ForgeActionPayload) => void) | null = null;

  setOnAction(callback: (payload: ForgeActionPayload) => void): void { this.onAction = callback; }

  open(player: PlayerState): void {
    if (this.overlay) this.overlay.remove();
    const exp = player.forgeExp ?? 0;
    const prog = getForgeProgress(exp);
    const level = prog.level;
    const rate = calcForgeRate(player);

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    let list = '';
    for (const r of FORGE_RECIPES) {
      const can = canForge(player, r);
      const missing = hasForgeIngredients(player, r);
      const ok = missing.length === 0;

      let ingHtml = '';
      for (const ing of r.ingredients) {
        const owned = player.inventory.filter((i) => i.id === ing.itemId).length;
        ingHtml += `<span style="color:${owned >= ing.count ? 'var(--success)' : 'var(--danger)'};font-size:0.8em;">${ing.itemName}×${ing.count}(${owned}) </span>`;
      }

      const disabled = !can || !ok;
      const reason = !can ? `需要${r.minSkill}级` : '材料不足';

      list += `
        <div class="recipe-card">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:bold;">🔨 ${r.name}</span>
            <span style="font-size:0.75em;color:var(--text-secondary);">${r.desc}</span>
          </div>
          <div>${ingHtml}</div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;">
            <span style="font-size:0.8em;color:var(--text-secondary);">成功率${(rate*100).toFixed(0)}%</span>
            <button class="btn btn-small btn-primary" data-recipe="${r.id}" ${disabled ? 'disabled' : ''}>${disabled ? reason : '炼制'}</button>
          </div>
        </div>
      `;
    }

    this.overlay.innerHTML = `
      <div class="modal" style="max-width:500px;max-height:80vh;overflow-y:auto;">
        <h2>🔨 炼器炉</h2>
        <div style="margin-bottom:12px;color:var(--text-secondary);font-size:0.9em;">
          熟练度：<b style="color:var(--text-gold);">${level}级</b>
          <span style="font-size:0.8em;">（总经验${exp}）</span>
          <div style="margin-top:4px;">
            <div class="stat-bar" style="height:6px;"><div class="stat-bar-fill exp" style="width:${prog.percent}%"></div></div>
            <span style="font-size:0.7em;">${prog.current}/${prog.needed} 升级</span>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px;">${list}</div>
        <button class="btn btn-small" id="forge-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);
    this.overlay.querySelectorAll('button[data-recipe]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.recipe!;
        if (this.onAction) this.onAction({ action: 'craft', recipeId: id });
      });
    });
    document.getElementById('forge-close')?.addEventListener('click', () => { this.close(); if (this.onAction) this.onAction({ action: 'close' }); });
    this.overlay.addEventListener('click', (e) => { if (e.target === this.overlay) { this.close(); if (this.onAction) this.onAction({ action: 'close' }); } });
  }

  close(): void { if (this.overlay) { this.overlay.remove(); this.overlay = null; } }
  isOpen(): boolean { return this.overlay !== null; }
}
